import shp from "shpjs"
// @ts-ignore
import tj from "@mapbox/togeojson"
// @ts-ignore
import flatten from "geojson-flatten"
import JSZip, { JSZipObject } from "jszip"
import { Feature, feature as turfFeature } from "@turf/helpers"
import bbox from "@turf/bbox"
import area from "@turf/area"

import {
  SpatialProcessingResult,
  SpatialValidationError,
  SpatialFileInfo,
  SupportedGeometry,
  SPATIAL_ERROR_CODES,
  SUPPORTED_EXTENSIONS,
  MANDATORY_SHAPEFILE_EXTENSIONS,
  ALLOWED_GEOMETRY_TYPES,
  MAX_FILE_SIZE,
} from "../types/spatial"

/**
 * Create user-friendly error messages with suggestions
 */
const createUserFriendlyError = (
  code: string,
  technicalMessage: string,
  userMessage?: string,
  suggestions?: string[]
): SpatialValidationError => {
  return {
    code,
    message: technicalMessage,
    userMessage: userMessage || technicalMessage,
    recoverable: true,
    suggestions: suggestions || [],
  }
}

/**
 * Generate a unique ID for spatial features
 */
const generateFeatureId = (): string => {
  return "_" + Math.random().toString(36).substring(2, 11)
}

/**
 * Extract files from a ZIP archive
 */
const extractZipFiles = async (buffer: ArrayBuffer): Promise<JSZipObject[]> => {
  const zip = new JSZip()
  await zip.loadAsync(buffer)
  return zip.file(/.+/) || []
}

/**
 * Validate that a ZIP file contains all required shapefile components
 */
const validateShapefileStructure = (zipFiles: JSZipObject[]): boolean => {
  return MANDATORY_SHAPEFILE_EXTENSIONS.every((extension) =>
    zipFiles.some((file) => file.name.slice(-3).toLowerCase() === extension)
  )
}

/**
 * Check if shapefile uses deprecated National Atlas projection
 */
const isUsingDeprecatedProjection = async (
  zipFiles: JSZipObject[]
): Promise<boolean> => {
  try {
    const prjFile = zipFiles.find(
      (file) => file.name.slice(-3).toLowerCase() === "prj"
    )
    if (!prjFile) return false

    const prjContent = await prjFile.async("string")
    return prjContent.includes("US_National_Atlas_Equal_Area")
  } catch (error) {
    console.warn("Error reading projection file:", error)
    return false
  }
}

/**
 * Validate individual feature geometry
 */
const validateFeatureGeometry = (
  feature: Feature,
  index: number
): SpatialValidationError[] => {
  const errors: SpatialValidationError[] = []

  if (!feature) {
    errors.push({
      code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
      message: `Feature ${index + 1} is null or undefined`,
    })
    return errors
  }

  if (!feature.geometry) {
    errors.push({
      code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
      message: `Feature ${index + 1} has no geometry`,
    })
    return errors
  }

  const geometryType = feature.geometry.type
  // @ts-ignore - Type checking for geometry types
  if (!ALLOWED_GEOMETRY_TYPES.includes(geometryType)) {
    errors.push({
      code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
      message: `Feature ${
        index + 1
      } has unsupported geometry type "${geometryType}". Supported types: ${ALLOWED_GEOMETRY_TYPES.join(
        ", "
      )}`,
    })
    return errors
  }

  // Validate coordinate structure
  // @ts-ignore - Complex GeoJSON type handling
  if (
    // @ts-ignore - Complex GeoJSON type handling
    !feature.geometry.coordinates ||
    // @ts-ignore - Complex GeoJSON type handling
    !Array.isArray(feature.geometry.coordinates)
  ) {
    errors.push({
      code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
      message: `Feature ${index + 1} has invalid coordinate structure`,
    })
    return errors
  }

  // Additional validation for Polygon
  if (geometryType === "Polygon") {
    const coordinates = feature.geometry.coordinates
    if (coordinates.length === 0) {
      errors.push({
        code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
        message: `Feature ${index + 1} (Polygon) has no coordinate rings`,
      })
    } else {
      // Check each ring
      // @ts-ignore - Complex GeoJSON coordinate handling
      coordinates.forEach((ring, ringIndex) => {
        // @ts-ignore - Complex GeoJSON coordinate handling
        if (!Array.isArray(ring) || ring.length < 4) {
          errors.push({
            code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
            message: `Feature ${index + 1} (Polygon) ring ${
              ringIndex + 1
            } must have at least 4 coordinates`,
          })
        }
        // Check if ring is closed (first and last coordinates should be the same)
        // @ts-ignore - Complex GeoJSON coordinate handling
        if (ring.length >= 4) {
          // @ts-ignore - Complex GeoJSON coordinate handling
          const first = ring[0]
          // @ts-ignore - Complex GeoJSON coordinate handling
          const last = ring[ring.length - 1]
          if (first[0] !== last[0] || first[1] !== last[1]) {
            console.warn(
              `Feature ${index + 1} (Polygon) ring ${
                ringIndex + 1
              } is not closed - will be auto-closed during processing`
            )
          }
        }
      })
    }
  }

  // Additional validation for MultiPolygon
  if (geometryType === "MultiPolygon") {
    const coordinates = feature.geometry.coordinates
    if (coordinates.length === 0) {
      errors.push({
        code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
        message: `Feature ${index + 1} (MultiPolygon) has no polygons`,
      })
    } else {
      coordinates.forEach((polygon, polygonIndex) => {
        if (!Array.isArray(polygon) || polygon.length === 0) {
          errors.push({
            code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
            message: `Feature ${index + 1} (MultiPolygon) polygon ${
              polygonIndex + 1
            } has no rings`,
          })
        }
      })
    }
  }

  return errors
}

/**
 * Filter features to only include supported geometry types with detailed validation
 */
const filterSupportedFeatures = (
  features: Feature[]
): { validFeatures: Feature[]; errors: SpatialValidationError[] } => {
  const validFeatures: Feature[] = []
  const allErrors: SpatialValidationError[] = []

  features.forEach((feature, index) => {
    const featureErrors = validateFeatureGeometry(feature, index)

    if (featureErrors.length === 0) {
      // Feature is valid, add it with a unique ID
      validFeatures.push({
        ...feature,
        id: generateFeatureId(),
      })
    } else {
      // Collect errors but don't fail completely - some features might be valid
      allErrors.push(...featureErrors)
    }
  })

  return { validFeatures, errors: allErrors }
}

/**
 * Convert MultiPolygon features to individual Polygon features
 */
const convertMultiPolygonToPolygons = (feature: Feature): Feature[] => {
  if (feature?.geometry?.type === "MultiPolygon") {
    const results: Feature[] = []
    // @ts-ignore - Complex GeoJSON coordinate handling
    feature.geometry.coordinates.forEach((coords) => {
      results.push(
        // @ts-ignore - Complex GeoJSON type conversion
        turfFeature(
          {
            type: "Polygon",
            coordinates: coords,
          },
          feature.properties,
          {
            id: generateFeatureId(),
          }
        )
      )
    })
    return results
  }
  return [feature]
}

/**
 * Remove Z-coordinates from feature coordinates to ensure 2D compatibility
 */
const dropZCoordinates = (feature: Feature): Feature => {
  if (
    // @ts-ignore - Complex GeoJSON type handling
    !feature?.geometry?.coordinates ||
    // @ts-ignore - Complex GeoJSON type handling
    feature.geometry.coordinates.length === 0
  ) {
    return feature
  }

  // Handle Polygon coordinates
  if (feature.geometry.type === "Polygon") {
    // @ts-ignore - Complex GeoJSON coordinate handling
    feature.geometry.coordinates.forEach((ring: any[]) => {
      ring.forEach((coord: any[]) => {
        if (coord.length === 3) {
          coord.pop() // Remove Z coordinate
        }
      })
    })
  }

  return feature
}

/**
 * Process KML file and extract spatial features
 */
const processKmlFile = async (file: File): Promise<Feature[]> => {
  const kmlText = await file.text()
  const parser = new DOMParser()
  const kml = parser.parseFromString(kmlText, "text/xml")

  // Check for XML parsing errors
  const parserError = kml.querySelector("parsererror")
  if (parserError) {
    throw new Error("Invalid KML file format")
  }

  const geojson = flatten(tj.kml(kml))
  return geojson.features || []
}

/**
 * Process shapefile (ZIP) and extract spatial features
 */
const processShapefileZip = async (file: File): Promise<Feature[]> => {
  const buffer = await file.arrayBuffer()
  const zipFiles = await extractZipFiles(buffer)

  if (!validateShapefileStructure(zipFiles)) {
    const error = createUserFriendlyError(
      SPATIAL_ERROR_CODES.MISSING_SHAPEFILE_COMPONENTS,
      `Shapefile must contain: ${MANDATORY_SHAPEFILE_EXTENSIONS.join(
        ", "
      )} files`,
      "Your shapefile is missing required components.",
      [
        `Make sure your ZIP file contains all these files: ${MANDATORY_SHAPEFILE_EXTENSIONS.map(
          (ext) => `.${ext}`
        ).join(", ")}`,
        "Export your shapefile again from your GIS software",
        "Check that all files have the same base name (e.g., boundaries.shp, boundaries.shx, etc.)",
        "Try using a KML file instead for simpler boundary definitions",
      ]
    )
    throw new Error(error.message)
  }

  if (await isUsingDeprecatedProjection(zipFiles)) {
    const error = createUserFriendlyError(
      SPATIAL_ERROR_CODES.INVALID_COORDINATE_SYSTEM,
      "National Atlas (EPSG:2163) projection is deprecated. Please reproject to WGS 84 or another coordinate system.",
      "Your shapefile uses an outdated coordinate system that we can't process.",
      [
        "Reproject your data to WGS 84 (EPSG:4326) coordinate system",
        "Use Web Mercator (EPSG:3857) projection as an alternative",
        "Contact your GIS administrator for help with reprojecting data",
        "Export your data as KML format instead, which uses WGS 84 by default",
      ]
    )
    throw new Error(error.message)
  }

  const geojson = await shp(buffer)
  // Handle both single FeatureCollection and array of FeatureCollections
  if (Array.isArray(geojson)) {
    // @ts-ignore - GeoJSON library type compatibility
    return geojson.flatMap((fc) => fc.features || [])
  }
  // @ts-ignore - GeoJSON library type compatibility
  return geojson.features || []
}

/**
 * Get basic file information
 */
export const getSpatialFileInfo = (file: File): SpatialFileInfo => {
  const extension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf("."))

  return {
    name: file.name,
    size: file.size,
    type: extension === ".kml" ? "kml" : "shapefile",
    extension,
  }
}

/**
 * Validate file extension and MIME type
 */
const validateFileFormat = (file: File): SpatialValidationError[] => {
  const errors: SpatialValidationError[] = []
  const extension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf("."))

  // Check file extension
  if (!SUPPORTED_EXTENSIONS.includes(extension as ".kml" | ".zip")) {
    errors.push(
      createUserFriendlyError(
        SPATIAL_ERROR_CODES.INVALID_FILE_TYPE,
        `Unsupported file extension "${extension}". Use ${SUPPORTED_EXTENSIONS.join(
          " or "
        )} files only`,
        `This file type (${extension}) is not supported.`,
        [
          "Use a KML file (.kml) for simple boundary definitions",
          "Use a zipped shapefile (.zip) containing all required components",
          "Make sure your file has the correct extension",
        ]
      )
    )
    return errors // Don't continue validation if extension is invalid
  }

  // Additional MIME type validation for KML files
  if (extension === ".kml") {
    const validKmlMimeTypes = [
      "application/vnd.google-earth.kml+xml",
      "text/xml",
      "application/xml",
      "text/plain", // Some systems may serve KML as plain text
    ]

    if (file.type && !validKmlMimeTypes.includes(file.type)) {
      console.warn(`KML file has unexpected MIME type: ${file.type}`)
      // Don't error out, just warn - some systems set incorrect MIME types
    }
  }

  // Additional MIME type validation for ZIP files (shapefiles)
  if (extension === ".zip") {
    const validZipMimeTypes = [
      "application/zip",
      "application/x-zip-compressed",
      "application/octet-stream", // Generic binary file type
    ]

    if (file.type && !validZipMimeTypes.includes(file.type)) {
      console.warn(`ZIP file has unexpected MIME type: ${file.type}`)
      // Don't error out, just warn
    }
  }

  return errors
}

/**
 * Check if file name contains valid characters and patterns
 */
const validateFileName = (file: File): SpatialValidationError[] => {
  const errors: SpatialValidationError[] = []

  // Check for empty filename
  if (!file.name || file.name.trim().length === 0) {
    errors.push({
      code: SPATIAL_ERROR_CODES.INVALID_FILE_TYPE,
      message: "File name is required",
    })
    return errors
  }

  // Check for dangerous characters (security consideration)
  // eslint-disable-next-line no-control-regex
  const dangerousChars = /[<>:"|?*\u0000-\u001f]/
  if (dangerousChars.test(file.name)) {
    errors.push({
      code: SPATIAL_ERROR_CODES.INVALID_FILE_TYPE,
      message: "File name contains invalid characters",
    })
  }

  // Check filename length (reasonable limit)
  if (file.name.length > 255) {
    errors.push({
      code: SPATIAL_ERROR_CODES.INVALID_FILE_TYPE,
      message: "File name is too long (maximum 255 characters)",
    })
  }

  return errors
}

/**
 * Validate spatial file before processing
 */
export const validateSpatialFile = (file: File): SpatialValidationError[] => {
  const errors: SpatialValidationError[] = []

  // Basic file validation
  if (!file) {
    errors.push({
      code: SPATIAL_ERROR_CODES.INVALID_FILE_TYPE,
      message: "No file provided",
    })
    return errors
  }

  // Validate file name
  errors.push(...validateFileName(file))

  // Validate file format
  errors.push(...validateFileFormat(file))

  // Check file size
  if (file.size === 0) {
    errors.push(
      createUserFriendlyError(
        SPATIAL_ERROR_CODES.INVALID_FILE_TYPE,
        "File is empty",
        "The uploaded file appears to be empty.",
        [
          "Make sure you selected the correct file",
          "Check that the file isn't corrupted",
          "Try exporting your spatial data again",
        ]
      )
    )
  } else if (file.size > MAX_FILE_SIZE) {
    const fileSizeMB = Math.round(file.size / 1024 / 1024)
    errors.push(
      createUserFriendlyError(
        SPATIAL_ERROR_CODES.FILE_TOO_LARGE,
        `File size (${fileSizeMB}MB) exceeds 10MB limit`,
        `Your file is too large (${fileSizeMB}MB). The maximum size is 10MB.`,
        [
          "Try simplifying your spatial data by reducing the number of points",
          "Remove unnecessary attributes or properties from your shapefile",
          "Split complex boundaries into smaller, separate files",
          "Use a different compression format or tool for your shapefile",
        ]
      )
    )
  }

  return errors
}

/**
 * Process spatial file and extract boundary features
 */
export const processSpatialFile = async (
  file: File
): Promise<SpatialProcessingResult> => {
  try {
    // Validate file first
    const validationErrors = validateSpatialFile(file)
    if (validationErrors.length > 0) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw validationErrors[0]
    }

    const fileInfo = getSpatialFileInfo(file)
    let rawFeatures: Feature[] = []

    // Process based on file type
    if (fileInfo.type === "kml") {
      rawFeatures = await processKmlFile(file)
    } else if (fileInfo.type === "shapefile") {
      rawFeatures = await processShapefileZip(file)
    }

    // Filter and validate features
    const { validFeatures, errors: featureValidationErrors } =
      filterSupportedFeatures(rawFeatures)

    // Log validation warnings but continue processing if we have some valid features
    if (featureValidationErrors.length > 0) {
      console.warn(
        "Some features had validation issues:",
        featureValidationErrors
      )
    }

    // Process valid features
    const processedFeatures = validFeatures
      .flatMap(convertMultiPolygonToPolygons)
      .map(dropZCoordinates)

    if (processedFeatures.length === 0) {
      const error: SpatialValidationError = {
        code: SPATIAL_ERROR_CODES.NO_VALID_FEATURES,
        message: `No supported spatial features found. Supported types: ${ALLOWED_GEOMETRY_TYPES.join(
          ", "
        )}`,
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw error
    }

    // Calculate metadata
    const featureCollection = {
      type: "FeatureCollection" as const,
      features: processedFeatures,
    }

    const boundingBox = bbox(featureCollection) as [
      number,
      number,
      number,
      number,
    ]
    const totalArea = area(featureCollection) // Area in square meters

    return {
      features: processedFeatures,
      metadata: {
        featureCount: processedFeatures.length,
        totalArea,
        boundingBox,
        coordinateSystem: "WGS 84 (assumed)", // TODO: Extract actual CRS info
      },
    }
  } catch (error) {
    // Re-throw SpatialValidationError as-is
    if (error && typeof error === "object" && "code" in error) {
      throw error
    }

    // Wrap other errors with more helpful messages
    let userMessage =
      "Failed to process spatial file. Please check your file format and try again."
    let suggestions: string[] = []

    if (error instanceof Error) {
      // Provide specific guidance for common error patterns
      if (error.message.includes("zip")) {
        userMessage =
          "Unable to read the ZIP file. Please ensure it's a valid shapefile archive."
        suggestions = [
          "Make sure the ZIP file isn't corrupted",
          "Re-export your shapefile from GIS software",
          "Try using a KML file instead",
        ]
      } else if (error.message.includes("shp")) {
        userMessage =
          "Issue reading shapefile components. Please check your shapefile structure."
        suggestions = [
          "Ensure all required files (.shp, .shx, .dbf, .prj) are included",
          "Check that all files have the same base name",
          "Try re-exporting from your GIS software",
        ]
      } else if (
        error.message.includes("xml") ||
        error.message.includes("kml")
      ) {
        userMessage = "Unable to parse KML file. Please check the file format."
        suggestions = [
          "Ensure the file is valid KML format",
          "Try opening in Google Earth to verify",
          "Re-export from your GIS software as KML",
        ]
      } else if (
        error.message.includes("projection") ||
        error.message.includes("coordinate")
      ) {
        userMessage = "Coordinate system issue detected."
        suggestions = [
          "Ensure your data uses WGS 84 (EPSG:4326) coordinate system",
          "Reproject your data to a supported coordinate system",
          "Contact support if you need help with coordinate systems",
        ]
      }
    }

    const wrappedError: SpatialValidationError = {
      code: SPATIAL_ERROR_CODES.PROCESSING_FAILED,
      message:
        error instanceof Error
          ? error.message
          : "Failed to process spatial file",
      userMessage,
      suggestions,
      details: error,
    }
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw wrappedError
  }
}

/**
 * Convert processed features to a format suitable for the sale boundary
 * Returns the first polygon feature as the primary boundary
 */
export const extractPrimaryBoundary = (
  result: SpatialProcessingResult
): SupportedGeometry | null => {
  const polygonFeatures = result.features.filter(
    (feature) =>
      feature.geometry.type === "Polygon" ||
      feature.geometry.type === "MultiPolygon"
  )

  if (polygonFeatures.length === 0) {
    return null
  }

  // Return the geometry of the first (or largest) polygon
  return polygonFeatures[0].geometry as SupportedGeometry
}

/**
 * Format error for user display with suggestions
 */
export const formatErrorForUser = (error: SpatialValidationError): string => {
  let message = error.userMessage || error.message

  if (error.suggestions && error.suggestions.length > 0) {
    message += "\n\nSuggestions:"
    error.suggestions.forEach((suggestion, index) => {
      message += `\n${index + 1}. ${suggestion}`
    })
  }

  return message
}

/**
 * Check if error is recoverable (user can potentially fix it)
 */
export const isRecoverableError = (error: SpatialValidationError): boolean => {
  return error.recoverable !== false // Default to recoverable unless explicitly set to false
}

/**
 * Get error suggestions as array
 */
export const getErrorSuggestions = (
  error: SpatialValidationError
): string[] => {
  return error.suggestions || []
}
